import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IonPopover, IonIcon } from '@ionic/react';
import { logOutOutline, arrowBackOutline } from 'ionicons/icons';
import AuthService from '../services/auth';
import { routesPath } from '../App';
import BreadcrumbDropdown from '../components/atoms/BreadcrumbDropdown/BreadcrumbDropdown';
import api, { accountApi } from '../services/api';
import './FullScreenLayout.css';

const FullScreenLayout = ({ user, breadcrumbs = [], children }) => {
  const navigate = useNavigate();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [collections, setCollections] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [loadingExecutions, setLoadingExecutions] = useState(false);

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
  };

  const fetchProjects = async () => {
    if (projects.length > 0) return;
    
    setLoadingProjects(true);
    try {
      const data = await api.get('/projects');
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const handleProjectsHover = () => {
    fetchProjects();
  };

  const handleProjectsClick = (path) => {
    navigate(path);
  };

  const fetchCollections = async (projectId) => {
    if (collections.length > 0) return;
    
    setLoadingCollections(true);
    try {
      const data = await accountApi.get(`/collections?projectId=${projectId}`);
      setCollections(data.collections || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
    } finally {
      setLoadingCollections(false);
    }
  };

  const handleCollectionsHover = (projectId) => {
    fetchCollections(projectId);
  };

  const handleCollectionsClick = (collectionId) => {
    // Navigate to project page with collection filter
    const projectId = breadcrumbs.find(b => b.projectId)?.projectId;
    if (projectId) {
      navigate(`/projects/${projectId}?collection=${collectionId}`);
    }
  };

  const fetchExecutions = async (collectionId) => {
    setLoadingExecutions(true);
    try {
      const projectId = breadcrumbs.find(b => b.projectId)?.projectId;
      
      const params = new URLSearchParams({
        projectId,
        limit: '50'
      });
      if (collectionId) {
        params.append('collection', collectionId);
      }
      
      const data = await api.get(`/executions?${params.toString()}`);
      setExecutions(data.executions || data.data || []);
    } catch (error) {
      console.error('Error fetching executions:', error);
    } finally {
      setLoadingExecutions(false);
    }
  };

  const handleExecutionsHover = (collectionId) => {
    fetchExecutions(collectionId);
  };

  const handleExecutionClick = (executionId) => {
    const projectId = breadcrumbs.find(b => b.projectId)?.projectId;
    if (projectId) {
      navigate(`/projects/${projectId}/runs/${executionId}`);
    }
  };

  const handleLogout = async () => {
    setIsPopoverOpen(false);
    try {
      AuthService.logout();
      console.log('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = routesPath.login;
    }
  };

  const handleBackClick = () => {
    // For execution details pages, navigate to test runs list
    const projectId = breadcrumbs.find(b => b.projectId)?.projectId;
    if (projectId) {
      navigate(`/projects/${projectId}/testruns`);
    } else {
      // Fallback to browser history
      navigate(-1);
    }
  };

  return (
    <div className="fullscreen-layout">
      <header className="fullscreen-header">
        <div className="fullscreen-header-content">
          {/* Back button */}
          <button className="back-button" onClick={handleBackClick} aria-label="Go back">
            <IonIcon icon={arrowBackOutline} />
          </button>
          
          <div className="fullscreen-breadcrumbs breadcrumb-notion">
            {breadcrumbs.map((crumb, index) => {
              const isLast = index === breadcrumbs.length - 1;
              const isProjects = crumb.label === 'Projects' && crumb.path === '/projects';
              const isProject = crumb.projectId;
              const isCollectionWithExecutions = crumb.isCollectionWithExecutions;
              
              return (
                <React.Fragment key={index}>
                  {isProjects ? (
                    <BreadcrumbDropdown
                      label={crumb.label}
                      isActive={isLast}
                      onFetch={handleProjectsHover}
                      items={projects}
                      loading={loadingProjects}
                      onItemClick={(project) => handleProjectsClick(`/projects/${project.projectId}`)}
                      onLabelClick={() => handleProjectsClick(crumb.path)}
                      getItemLabel={(project) => project.name}
                      getItemKey={(project) => project.projectId}
                      emptyMessage="No projects"
                      headerLabel="Projects"
                    />
                  ) : isProject ? (
                    <BreadcrumbDropdown
                      label={crumb.label}
                      isActive={isLast}
                      onFetch={() => handleCollectionsHover(crumb.projectId)}
                      items={collections}
                      loading={loadingCollections}
                      onItemClick={(collection) => handleCollectionsClick(collection.collectionId)}
                      onLabelClick={() => {
                        if (crumb.path) {
                          navigate(crumb.path);
                        }
                      }}
                      getItemLabel={(collection) => collection.collectionName || collection.name}
                      getItemKey={(collection) => collection.collectionId}
                      emptyMessage="No collections"
                      headerLabel="Collections"
                      isProject={true}
                    />
                  ) : isCollectionWithExecutions ? (
                    <BreadcrumbDropdown
                      label={crumb.label}
                      isActive={isLast}
                      onFetch={() => handleExecutionsHover(crumb.collectionId)}
                      items={executions}
                      loading={loadingExecutions}
                      onItemClick={(execution) => handleExecutionClick(execution.executionId)}
                      onLabelClick={() => {
                        // Navigate to project page with collection filter
                        const projectId = breadcrumbs.find(b => b.projectId)?.projectId;
                        if (projectId && crumb.collectionId) {
                          navigate(`/projects/${projectId}?collection=${crumb.collectionId}`);
                        }
                      }}
                      getItemLabel={(execution) => execution.testName || execution.executionId}
                      getItemKey={(execution) => execution.executionId}
                      emptyMessage="No executions"
                      headerLabel="Executions"
                      isProject={false}
                      isCollection={true}
                    />
                  ) : crumb.loading ? (
                    <div className="breadcrumb-item inactive">
                      <div className="breadcrumb-skeleton-line" />
                    </div>
                  ) : crumb.path ? (
                    <span 
                      className={`breadcrumb-item ${isLast ? 'active' : 'inactive'} ${crumb.isCollection ? 'breadcrumb-collection' : ''}`}
                      onClick={() => navigate(crumb.path)}
                    >
                      {crumb.label}
                    </span>
                  ) : (
                    <span className={`breadcrumb-item ${isLast ? 'active' : 'inactive'} ${crumb.isCollection ? 'breadcrumb-collection' : ''}`}>
                      {crumb.label}
                    </span>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          
          {user && (
            <>
              <div 
                className="fullscreen-user-avatar" 
                id="fullscreen-user-menu"
                onClick={() => setIsPopoverOpen(true)}
              >
                <div className={`avatar-circle avatar-${getInitials(user.name || user.email)?.[0]?.toUpperCase() || 'U'}`}>
                  {getInitials(user.name || user.email)}
                </div>
              </div>
              
              <IonPopover
                trigger="fullscreen-user-menu"
                isOpen={isPopoverOpen}
                onDidDismiss={() => setIsPopoverOpen(false)}
                showBackdrop={true}
              >
                <div className="user-menu-popover">
                  <div className="user-info border-bottom">
                    <div className="user-info-name">{user.name}</div>
                    <div className="user-info-email">{user.email}</div>
                  </div>
                  
                  <div className="menu-item logout-item" onClick={handleLogout}>
                    <span>Logout</span>
                    <IonIcon icon={logOutOutline} className="logout-icon" />
                  </div>
                </div>
              </IonPopover>
            </>
          )}
        </div>
      </header>

      <main className="fullscreen-main">
        {children}
      </main>
    </div>
  );
};

export default FullScreenLayout;

